"use client";

import { useState, useRef, useEffect } from "react";
import { Settings2 } from "lucide-react";
import type { ColumnDef } from "./types";

interface ColumnVisibilityToggleProps<T> {
  columns: ColumnDef<T>[];
  hiddenColumns: Set<string>;
  onToggle: (columnId: string) => void;
  /** Extra classes for the trigger button (e.g. "h-11" to match a taller row). */
  buttonClassName?: string;
}

export function ColumnVisibilityToggle<T>({
  columns,
  hiddenColumns,
  onToggle,
  buttonClassName = "",
}: ColumnVisibilityToggleProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hideableColumns = columns.filter(
    (c) => c.id && c.hideable !== false,
  );

  if (hideableColumns.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#2a475e]/50 bg-[#16202d]/50 text-[#c7d5e0] hover:bg-[#1f2e3f]/50 hover:border-[#3a556e] transition-colors ${buttonClassName}`}
      >
        <Settings2 className="w-4 h-4 text-[#8f98a0]" />
        <span className="text-sm font-medium">Columns</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] bg-[#1b2838] border border-[#2a475e] rounded-lg shadow-xl py-1.5">
          {hideableColumns.map((col) => (
            <label
              key={col.id}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-[#c7d5e0] hover:bg-[#1f2e3f] cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={!hiddenColumns.has(col.id!)}
                onChange={() => onToggle(col.id!)}
                className="accent-[#66c0f4] rounded"
              />
              {typeof col.header === "string" ? col.header : (col.label ?? col.id)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
